import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowDown, ArrowLeft, Link2, Share2 } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { Contact } from "@/components/site/Contact";
import { TrustBand } from "@/components/site/TrustBand";
import { MaskLines, Reveal, SectionLabel } from "@/components/site/Motion";
import { SectionCalc } from "@/components/site/SectionCalc";
import { LamellareFaq } from "@/components/site/LamellareFaq";
import { scrollToId } from "@/lib/scroll";
import { track } from "@/lib/track";
import { useLang, useT } from "@/i18n";
import { langUrl, useJsonLd, usePageMeta } from "@/lib/pageMeta";

const SITE = "https://www.emmaindustrie.it";
// [mode, anchor, title, intro]
const CALCS = [
  ["beam", "trave-appoggiata", "Trave appoggiata in legno lamellare", "Luce, interasse e carichi: stimiamo l'altezza minima della trave in lamellare (GL24h – GL32h) verificando flessione, taglio e freccia secondo NTC 2018 o Eurocodice 5."],
  ["floor", "solaio", "Solaio in legno lamellare", "Scegli la categoria d'uso NTC 2018: calcoliamo i carichi variabili e la sezione delle travi del solaio, con verifiche di resistenza e deformabilità."],
  ["roof", "copertura-a-falde", "Copertura a falde in legno", "Pendenza, zona e altitudine: calcoliamo il carico neve secondo NTC 2018 e la sezione delle travi della copertura in lamellare."],
  ["xlam", "solaio-xlam", "Solaio in pannelli X-LAM", "Luce e categoria d'uso: stimiamo lo spessore del pannello X-LAM (CLT) con il metodo gamma, verificando taglio per rotolamento e frecce."],
];

const share = async (url, title, t, where) => {
  track("share_calcolatori", { posizione: where });
  if (navigator.share) {
    try { await navigator.share({ title, url }); } catch { /* closed by the user */ }
    return;
  }
  try {
    await navigator.clipboard.writeText(url);
    toast.success(t("calcs.copied", "Link copiato: incollalo in una email o in chat."));
  } catch {
    toast.error(url);
  }
};

const Index = ({ t, urlOf }) => (
  <div className="mt-16 grid gap-px bg-white/10 sm:grid-cols-2 lg:grid-cols-4" data-testid="calcs-index">
    {CALCS.map(([m, anchor, title], i) => (
      <div key={m} className="flex flex-col justify-between gap-6 bg-[#1A1714] p-6" data-testid={`calcs-card-${m}`}>
        <div>
          <p className="font-mono text-xs text-[#D98A52]">0{i + 1}</p>
          <p className="mt-2 font-display text-xl font-bold">{t(`calcs.${m}.h`, title)}</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => scrollToId(`#${anchor}`)} className="tap inline-flex items-center gap-2 rounded-full border border-[#D98A52] px-4 py-2 text-sm transition-colors duration-300 hover:bg-[#D98A52]/15" data-testid={`calcs-open-${m}`}>
            {t("calcs.open", "Apri")} <ArrowDown className="h-4 w-4" />
          </button>
          <button onClick={() => share(urlOf(anchor), t(`calcs.${m}.h`, title), t, m)} aria-label={t("calcs.shareOne", "Condividi questo calcolatore")} className="tap rounded-full border border-white/20 p-2 text-[#D6D0C4] transition-colors duration-300 hover:border-[#D98A52] hover:text-[#D98A52]" data-testid={`calcs-share-${m}`}>
            <Link2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    ))}
  </div>
);

const Hero = ({ t, urlOf }) => (
  <section className="px-5 pb-20 pt-10 sm:px-8" data-testid="calcs-hero">
    <div className="mx-auto max-w-[1500px]">
      <Link to="/" className="tap inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-[#D6D0C4] transition-colors duration-300 hover:text-[#D98A52]" data-testid="calcs-back">
        <ArrowLeft className="h-4 w-4" /> {t("beachPage.back", "Torna alla home")}
      </Link>
      <div className="mt-14"><SectionLabel index="NTC 2018 · EC5" dark>{t("calcs.label", "Strumenti per progettisti")}</SectionLabel></div>
      <div className="mt-10 grid gap-10 lg:grid-cols-12 lg:items-end">
        <MaskLines as="h1" onLoad className="font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:col-span-7 lg:text-6xl"
          lines={[t("calcs.h1a", "Calcolatori strutturali"), <em key="e" className="font-serif font-normal text-[#D98A52]">{t("calcs.h1b", "per il legno.")}</em>]} />
        <Reveal delay={0.2} className="max-w-md lg:col-span-5">
          <p className="text-base leading-relaxed text-[#D6D0C4] sm:text-lg" data-testid="calcs-intro">{t("calcs.intro", "Quattro calcolatori gratuiti per il predimensionamento di travi, solai e coperture in legno lamellare e pannelli X-LAM. Calcola online, scarica il PDF e condividi il link con il tuo team.")}</p>
          <button onClick={() => share(urlOf(""), t("calcs.metaTitle", "Calcolatori strutturali per legno lamellare e X-LAM"), t, "pagina")} className="btn-amber mt-8" data-testid="calcs-share-page">
            {t("calcs.share", "Condividi i calcolatori")} <Share2 className="h-4 w-4" />
          </button>
        </Reveal>
      </div>
      <Index t={t} urlOf={urlOf} />
    </div>
  </section>
);

export default function Calcolatori() {
  const t = useT();
  const { lang } = useLang();
  const urlOf = (anchor) => `${langUrl(window.location.origin, "/calcolatori", lang)}${anchor ? `#${anchor}` : ""}`;
  usePageMeta({
    title: t("calcs.metaTitle", "Calcolatori strutturali per legno lamellare e X-LAM"),
    desc: t("calcs.metaDesc", "Calcolatori online gratuiti per progettisti: trave appoggiata, solaio e copertura a falde in legno lamellare, solaio in pannelli X-LAM. Verifiche NTC 2018 ed Eurocodice 5, PDF scaricabile."),
    path: "/calcolatori",
    image: "/og-lamellare.jpg",
    brand: "EMMA INDUSTRIE",
  });
  useJsonLd("calcolatori", {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: t("calcs.metaTitle", "Calcolatori strutturali per legno lamellare e X-LAM"),
    itemListElement: CALCS.map(([m, anchor, title, intro], i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "WebApplication",
        name: t(`calcs.${m}.h`, title),
        description: t(`calcs.${m}.p`, intro),
        url: `${langUrl(SITE, "/calcolatori", lang)}#${anchor}`,
        applicationCategory: "EngineeringApplication",
        operatingSystem: "Web",
        inLanguage: lang,
        isAccessibleForFree: true,
        offers: { "@type": "Offer", price: "0", priceCurrency: "EUR" },
        provider: { "@type": "Organization", name: "EMMA INDUSTRIE", url: SITE },
      },
    })),
  });
  return (
    <div className="bg-[#1A1714] text-[#F3EFE6]" data-testid="calcs-page">
      <Nav />
      <main className="pt-36">
        <Hero t={t} urlOf={urlOf} />
        {CALCS.map(([m, anchor, title, intro]) => (
          <SectionCalc key={m} fixed initialMode={m} id={anchor} title={t(`calcs.${m}.h`, title)} intro={t(`calcs.${m}.p`, intro)} />
        ))}
        <LamellareFaq dataKey="faqCalc" titleKey="calcs.faqH" title="Domande frequenti sui calcolatori" />
        <TrustBand />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
